/**
 * useRelativeDate - locale-aware relative timestamp formatter.
 *
 * Wraps date-fns `formatDistanceToNow` with the active UI locale so feeds like
 * "about 2 hours ago" are translated (he/es/en) instead of always English.
 * Prefer this over calling `formatDistanceToNow` directly — a raw call with no
 * `locale` option silently renders English and the no-literal-string linter
 * cannot catch it.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { getDateFnsLocale } from '../utils/dateFnsLocale';

export interface RelativeDateOptions {
  /** Append a suffix/prefix ("ago" / "in"). Defaults to true. */
  addSuffix?: boolean;
}

export type RelativeDateFormatter = (
  date: Date | string | number,
  options?: RelativeDateOptions
) => string;

/**
 * Returns a formatter that renders a date relative to now in the active locale.
 */
export function useRelativeDate(): RelativeDateFormatter {
  const { i18n } = useTranslation();
  const locale = getDateFnsLocale(i18n.language);

  return useCallback(
    (date: Date | string | number, options?: RelativeDateOptions) =>
      formatDistanceToNow(typeof date === 'string' ? new Date(date) : date, {
        addSuffix: options?.addSuffix ?? true,
        locale,
      }),
    [locale]
  );
}
